import { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { StateContext } from "../context/globalContext/context";
import { iGlobalContext } from "../context/globalContext/interface";
import { ROUTES } from "../constant";
import { ScreenTypes } from "../enums";
import {
  BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT,
  INITIAL_BREADCRUMBS_LIST,
} from "../constant/bookingInProgressConstant/breadCrumbsConstant";
import { getSharedCartAPI, getSharedCartDetailsAPI, iSharedCartPayload } from "../api/sharedCart/sharedCart";
import { mapSharedCartDetails } from "../utils/sharedCart/mapSharedCartDetails";
import { savePostLoginRedirect } from "./usePostLoginRedirect";
import {
  getSharedCartIdFromUrl,
  markSharedCartView,
  clearSharedCartView,
  setSharedByUserId,
} from "./useIsSharedCartView";
import { consoleLog } from "../utils/consoleLog/consoleLog";

/**
 * Recipient bootstrap for a shared cart. Mounted once at app level (App.tsx).
 *
 * `?sharedCartId=<id>` reuses the EXISTING booking/review flow (no dedicated route):
 *   - NOT logged in → redirect to login/onboarding; usePostLoginRedirect returns
 *     the user to the shared link after sign-in.
 *   - logged in → flip `sharedCartLoading` (App shows the review-page skeleton),
 *     fetch the stored snapshot, then `/search/shared-details` which returns the
 *     flight + hotels + an embedded `priceReview`. We DO NOT call `/price/review`
 *     for shared opens — `data.priceReview` is used directly. The response is
 *     mapped into the booking state and we land on the review step.
 */
export function useSharedCartBootstrap() {
  const {
    accessJWT,
    userInfo,
    isMobile,
    setSharedCartLoading,
    setPromoReviewCartData,
    setFlightData,
    setFlightAvailabilityData,
    setSelectedFlightOptions,
    setBookingDetails,
    setSelectedHotelRoomArr,
    setHotelData,
    setScreen,
    setActiveBreadCrumbs,
    setBreadList,
  }: iGlobalContext = useContext(StateContext);
  const location = useLocation();
  const navigate = useNavigate();
  const handledRef = useRef(false);
  const redirectedToLoginRef = useRef(false);

  const isLoggedIn = !!userInfo?.loggedIn && !!userInfo?.id;

  useEffect(() => {
    const sharedCartId = getSharedCartIdFromUrl(location.search);
    if (!sharedCartId) return;

    if (!isLoggedIn) {
      savePostLoginRedirect(window.location.pathname, window.location.search);
      if (!redirectedToLoginRef.current) {
        redirectedToLoginRef.current = true;
        navigate(ROUTES.onBoarding); // clean path — do NOT carry ?sharedCartId here
      }
      return;
    }

    if (handledRef.current) return;
    handledRef.current = true;
    markSharedCartView();
    setSharedCartLoading(true); // App shows the review-page skeleton
    void load(sharedCartId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, location.search]);

  // Build the breadcrumb list for the review step (only the legs present in the
  // cart), with the review crumb active.
  function buildReviewBreadList(opts: { flights: boolean; hotels: boolean }) {
    const list = INITIAL_BREADCRUMBS_LIST.filter((c: any) => {
      if (c.value === BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT.Flights) return opts.flights;
      if (c.value === BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT.Hotels) return opts.hotels;
      return true; // ReviewNew
    }).map((c: any) => ({
      ...c,
      checked: true,
      activeCrumbs: c.value === BOOKING_IN_PROGRESS_BREADCRUMBS_TEXT.ReviewNew,
    }));
    return list;
  }

  async function load(sharedCartId: string) {
    try {
      // 1) Stored snapshot (the shared-details payload).
      const stored = await getSharedCartAPI(accessJWT, sharedCartId);
      const snapshot: iSharedCartPayload | undefined = stored?.data?.data;
      if (!snapshot) {
        consoleLog(["SHARED CART: snapshot not found", sharedCartId]);
        return; // stay on skeleton (interim)
      }
      // Remember who shared this cart (the SharedCart row's userId) so the
      // recipient's post-prebook itinerary save can record `sharedByUserId`.
      setSharedByUserId(stored?.data?.userId);

      // 2) Reconstruct flight + hotels + embedded priceReview (NO /price/review).
      const res = await getSharedCartDetailsAPI(accessJWT, snapshot);
      consoleLog(["SHARED CART DETAILS RESPONSE", res]);
      // Backend proxy wraps the engine body in SuccessResponse.data; the engine
      // body itself nests the payload under `.data`. Tolerate both.
      const data = res?.data?.data ?? res?.data;
      if (!data || (!data.flight && !(Array.isArray(data.hotels) && data.hotels.length) && !data.priceReview)) {
        consoleLog(["SHARED CART: empty/invalid details", res]);
        return; // stay on skeleton
      }

      const mapped = mapSharedCartDetails(data, snapshot);

      // 3) Hydrate the booking state the review page reads.
      if (mapped.promoReviewCartData) setPromoReviewCartData(mapped.promoReviewCartData);
      setSelectedFlightOptions(mapped.selectedFlightOptions);
      if (mapped.flightData) setFlightData(mapped.flightData);
      if (mapped.flightAvailabilityData) setFlightAvailabilityData(mapped.flightAvailabilityData);
      setSelectedHotelRoomArr(mapped.selectedHotelRoomArr);
      setHotelData(mapped.hotelData);
      if (Object.keys(mapped.bookingPatch).length) {
        setBookingDetails((prev: any) => ({ ...prev, ...mapped.bookingPatch }));
      }

      // 4) Land on the review step (ReviewNew) and reveal it.
      const breadList = buildReviewBreadList(mapped.selectedFlightOptions);
      setBreadList(breadList);
      setActiveBreadCrumbs(breadList[breadList.length - 1]);
      setScreen(ScreenTypes.newBooking);
      if (isMobile) navigate(ROUTES.BookingInProgress); // /booking (mobile full-page)

      setSharedCartLoading(false); // swap skeleton → real review
    } catch (e) {
      consoleLog(["SHARED CART BOOTSTRAP ERROR", e]);
      clearSharedCartView();
      setSharedCartLoading(false);
    }
  }
}
